import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api.service';
import { CustomerValidationsPanel } from '../components/CustomerValidationsPanel';
import { CustomerOriginationDocs } from '../components/CustomerOriginationDocs';
import { FEATURES } from '../config/features';

type Tab = 'info' | 'loans' | 'payments' | 'kyc' | 'bureau' | 'validations';

const TABS: { id: Tab; label: string }[] = [
  { id: 'info', label: 'Información' },
  { id: 'loans', label: 'Préstamos' },
  { id: 'payments', label: 'Pagos' },
  ...(FEATURES.kyc ? [{ id: 'kyc' as const, label: 'KYC' }] : []),
  { id: 'bureau', label: 'Buró' },
  { id: 'validations', label: 'Validaciones' },
];

const STATUS: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  APPROVED: 'bg-blue-100 text-blue-800',
  ACTIVE: 'bg-green-100 text-green-800',
  COMPLETED: 'bg-gray-100 text-gray-800',
  REJECTED: 'bg-red-100 text-red-800',
  DEFAULTED: 'bg-red-100 text-red-800',
  CANCELLED: 'bg-gray-100 text-gray-700',
};

function money(n: number | null | undefined) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return `$${Number(n).toLocaleString('es-AR')}`;
}

/** Acepta 999256.28, 999256,28, 999.256,28 o 999,256.28 */
function parseMoneyInput(raw: string): number | undefined {
  const s = raw.trim().replace(/[^\d.,\-]/g, '');
  if (!s) return undefined;
  let normalized = s;
  if (normalized.includes(',') && normalized.includes('.')) {
    if (normalized.lastIndexOf(',') > normalized.lastIndexOf('.')) {
      normalized = normalized.replace(/\./g, '').replace(',', '.');
    } else {
      normalized = normalized.replace(/,/g, '');
    }
  } else if (normalized.includes(',')) {
    const parts = normalized.split(',');
    if (parts.length === 2 && parts[1].length <= 2) {
      normalized = `${parts[0].replace(/\./g, '')}.${parts[1]}`;
    } else {
      normalized = normalized.replace(/,/g, '');
    }
  }
  const n = Number(normalized);
  return Number.isFinite(n) ? n : undefined;
}

/** Referencia legible de un crédito (nº, monto, fecha). */
function loanLabel(loan: any): string {
  if (!loan) return '—';
  const num = loan.loanNumber?.trim() || `#${String(loan.id ?? '').slice(-6).toUpperCase()}`;
  const date = (loan.disbursementDate ?? loan.createdAt)?.slice?.(0, 10) ?? '—';
  const product = loan.product?.shortName || loan.product?.name || loan.product?.planCode;
  const parts = [num, money(loan.amount), date];
  if (product) parts.splice(1, 0, product);
  return parts.join(' · ');
}

function loanShortRef(loan: any): string {
  if (!loan) return '—';
  return loan.loanNumber?.trim() || `#${String(loan.id ?? '').slice(-6).toUpperCase()}`;
}

export function CustomerDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<any | null>(null);
  const [paymentsTotal, setPaymentsTotal] = useState(0);
  const [kycChecks, setKycChecks] = useState<any[]>([]);
  const [bureauProvider, setBureauProvider] = useState('VERAZ');
  const [bureauQueries, setBureauQueries] = useState<any[]>([]);
  const [bureauLoading, setBureauLoading] = useState(false);
  const [bureauError, setBureauError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('info');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [c, customerKyc, paymentsPage] = await Promise.all([
        apiService.getCustomer(id),
        FEATURES.kyc ? apiService.getKycChecks(id) : Promise.resolve([]),
        apiService.listPayments({ customerId: id, page: 1, pageSize: 1 }),
      ]);
      setCustomer(c);
      setKycChecks(customerKyc);
      setPaymentsTotal(paymentsPage.meta?.total ?? 0);
      if (c.documentNumber) {
        const queries = await apiService.listBureauQueries(c.documentNumber);
        setBureauQueries(queries);
      } else {
        setBureauQueries([]);
      }
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [id]);

  if (loading) return <div className="p-6 text-gray-500">Cargando…</div>;
  if (error) return <div className="p-6"><div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div></div>;
  if (!customer) return null;

  return (
    <div className="p-6">
      <button onClick={() => navigate('/customers')} className="text-blue-600 hover:underline text-sm mb-4">← Volver a clientes</button>

      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{customer.firstName} {customer.lastName}</h1>
          <p className="text-gray-600">{customer.email} · {customer.phone ?? '—'}</p>
          <p className="text-xs text-gray-500 font-mono mt-1">{customer.id}</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          {customer.riskLevel && (
            <span className={'inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ' +
              ({ LOW: 'bg-green-100 text-green-800', MEDIUM: 'bg-yellow-100 text-yellow-800', HIGH: 'bg-red-100 text-red-800' }[customer.riskLevel as 'LOW' | 'MEDIUM' | 'HIGH'] ?? 'bg-gray-100 text-gray-800')
            }>Riesgo {customer.riskLevel}</span>
          )}
          {FEATURES.kyc && customer.kycStatus && (
            <span className={'inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ' + (STATUS[customer.kycStatus] ?? 'bg-gray-100 text-gray-800')}>
              KYC {customer.kycStatus}
            </span>
          )}
        </div>
      </div>

      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-4">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={'px-4 py-2 text-sm font-medium border-b-2 ' +
                (tab === t.id ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-900')
              }>
              {t.label}
              {t.id === 'loans' && customer.loans && <span className="ml-1 text-xs text-gray-400">({customer.loans.length})</span>}
              {t.id === 'payments' && <span className="ml-1 text-xs text-gray-400">({paymentsTotal})</span>}
              {t.id === 'kyc' && <span className="ml-1 text-xs text-gray-400">({kycChecks.length})</span>}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'info' && <InfoTab customer={customer} onSaved={load} />}
      {tab === 'loans' && <LoansTab loans={customer.loans ?? []} />}
      {tab === 'payments' && (
        <PaymentsTab
          customerId={customer.id}
          loans={customer.loans ?? []}
          onTotalChange={setPaymentsTotal}
        />
      )}
      {FEATURES.kyc && tab === 'kyc' && <KycTab kycChecks={kycChecks} />}
      {tab === 'bureau' && (
        <BureauTab
          customer={customer}
          queries={bureauQueries}
          loading={bureauLoading}
          error={bureauError}
          provider={bureauProvider}
          onProviderChange={setBureauProvider}
          onQuery={async () => {
            if (!customer.documentNumber) {
              setBureauError('El cliente no tiene DNI/CUIT cargado');
              return;
            }
            setBureauLoading(true);
            setBureauError(null);
            try {
              await apiService.queryBureau(customer.documentNumber, bureauProvider);
              const queries = await apiService.listBureauQueries(customer.documentNumber);
              setBureauQueries(queries);
            } catch (e: any) {
              setBureauError(e?.response?.data?.message ?? e.message);
            } finally {
              setBureauLoading(false);
            }
          }}
        />
      )}
      {tab === 'validations' && <CustomerValidationsPanel customerId={customer.id} />}
    </div>
  );
}

function Row({ label, value }: { label: string; value: any }) {
  return (
    <div className="flex justify-between border-b border-gray-100 py-2">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm text-gray-900">{value ?? '—'}</span>
    </div>
  );
}

function PayslipDerivedRows({ customerId, refreshToken }: { customerId: string; refreshToken?: string | number }) {
  const [period, setPeriod] = useState<string | null>(null);
  const [payday, setPayday] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiService
      .listOriginationDocuments(customerId)
      .then((docs) => {
        if (cancelled) return;
        const payslips = docs
          .filter((d: any) => d.kind === 'PAYSLIP' && d.confirmedAt)
          .sort((a: any, b: any) => String(b.confirmedAt).localeCompare(String(a.confirmedAt)));
        const last = payslips[0];
        setPeriod(last?.confirmedPeriod ?? null);
        setPayday(last?.confirmedPayday ?? null);
      })
      .catch(() => {
        if (!cancelled) {
          setPeriod(null);
          setPayday(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [customerId, refreshToken]);

  return (
    <>
      <Row label="Período recibo" value={period} />
      <Row label="Día de cobro" value={payday} />
    </>
  );
}

function InfoTab({ customer, onSaved }: { customer: any; onSaved: () => Promise<void> }) {
  const [editingPersonal, setEditingPersonal] = useState(false);
  const [editingAddress, setEditingAddress] = useState(false);
  const [editingFiscal, setEditingFiscal] = useState(false);
  const [editingFinancial, setEditingFinancial] = useState(false);
  const [editingBank, setEditingBank] = useState(false);
  const [personalForm, setPersonalForm] = useState({
    firstName: customer.firstName ?? '',
    lastName: customer.lastName ?? '',
    email: customer.email ?? '',
    phone: customer.phone ?? '',
    birthDate: customer.birthDate?.slice(0, 10) ?? '',
    gender: customer.gender ?? 'X',
    documentType: customer.documentType ?? 'DNI',
    documentNumber: customer.documentNumber ?? '',
  });
  const [addressForm, setAddressForm] = useState({
    address: customer.address ?? '',
    city: customer.city ?? '',
    state: customer.state ?? '',
    zipCode: customer.zipCode ?? '',
    country: customer.country ?? '',
  });
  const [fiscalForm, setFiscalForm] = useState({
    taxId: customer.taxId ?? '',
    ivaCondition: customer.ivaCondition ?? 'CONSUMIDOR_FINAL',
  });
  const [bankForm, setBankForm] = useState({
    cbu: customer.cbu ?? '',
  });
  const [financialForm, setFinancialForm] = useState({
    monthlyIncome: customer.monthlyIncome != null ? String(customer.monthlyIncome) : '',
    monthlyExpenses: customer.monthlyExpenses != null ? String(customer.monthlyExpenses) : '',
    employmentStatus: customer.employmentStatus ?? '',
    employmentYears: customer.employmentYears != null ? String(customer.employmentYears) : '',
  });
  const [saving, setSaving] = useState(false);
  const [personalError, setPersonalError] = useState<string | null>(null);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [fiscalError, setFiscalError] = useState<string | null>(null);
  const [bankError, setBankError] = useState<string | null>(null);
  const [financialError, setFinancialError] = useState<string | null>(null);

  const IVA_OPTIONS = [
    { value: 'CONSUMIDOR_FINAL', label: 'Consumidor Final' },
    { value: 'RESP_INSCRIPTO', label: 'IVA Responsable Inscripto' },
    { value: 'MONOTRIBUTO', label: 'Monotributo' },
    { value: 'EXENTO', label: 'IVA Exento' },
    { value: 'NO_CATEGORIZADO', label: 'No Categorizado' },
  ];

  const EMPLOYMENT_OPTIONS = [
    { value: '', label: 'Sin indicar' },
    { value: 'EMPLEADO', label: 'Empleado' },
    { value: 'MONOTRIBUTO', label: 'Monotributo' },
    { value: 'AUTONOMO', label: 'Autónomo' },
    { value: 'JUBILADO', label: 'Jubilado' },
    { value: 'DESEMPLEADO', label: 'Desempleado' },
    { value: 'OTRO', label: 'Otro' },
  ];

  useEffect(() => {
    setPersonalForm({
      firstName: customer.firstName ?? '',
      lastName: customer.lastName ?? '',
      email: customer.email ?? '',
      phone: customer.phone ?? '',
      birthDate: customer.birthDate?.slice(0, 10) ?? '',
      gender: customer.gender ?? 'X',
      documentType: customer.documentType ?? 'DNI',
      documentNumber: customer.documentNumber ?? '',
    });
    setAddressForm({
      address: customer.address ?? '',
      city: customer.city ?? '',
      state: customer.state ?? '',
      zipCode: customer.zipCode ?? '',
      country: customer.country ?? '',
    });
    setFinancialForm({
      monthlyIncome: customer.monthlyIncome != null ? String(customer.monthlyIncome) : '',
      monthlyExpenses: customer.monthlyExpenses != null ? String(customer.monthlyExpenses) : '',
      employmentStatus: customer.employmentStatus ?? '',
      employmentYears: customer.employmentYears != null ? String(customer.employmentYears) : '',
    });
  }, [
    customer.id,
    customer.firstName,
    customer.lastName,
    customer.email,
    customer.phone,
    customer.birthDate,
    customer.gender,
    customer.documentType,
    customer.documentNumber,
    customer.address,
    customer.city,
    customer.state,
    customer.zipCode,
    customer.country,
    customer.monthlyIncome,
    customer.monthlyExpenses,
    customer.employmentStatus,
    customer.employmentYears,
  ]);

  const savePersonal = async () => {
    setSaving(true);
    setPersonalError(null);
    try {
      await apiService.updateCustomer(customer.id, {
        firstName: personalForm.firstName.trim(),
        lastName: personalForm.lastName.trim(),
        email: personalForm.email.trim(),
        phone: personalForm.phone.trim() || undefined,
        birthDate: personalForm.birthDate || undefined,
        gender: personalForm.gender,
        documentType: personalForm.documentType || undefined,
        documentNumber: personalForm.documentNumber.trim() || undefined,
      });
      setEditingPersonal(false);
      await onSaved();
    } catch (e: any) {
      setPersonalError(e?.response?.data?.message ?? e.message);
    } finally {
      setSaving(false);
    }
  };

  const saveAddress = async () => {
    setSaving(true);
    setAddressError(null);
    try {
      await apiService.updateCustomer(customer.id, {
        address: addressForm.address.trim() || undefined,
        city: addressForm.city.trim() || undefined,
        state: addressForm.state.trim() || undefined,
        zipCode: addressForm.zipCode.trim() || undefined,
        country: addressForm.country.trim() || undefined,
      });
      setEditingAddress(false);
      await onSaved();
    } catch (e: any) {
      setAddressError(e?.response?.data?.message ?? e.message);
    } finally {
      setSaving(false);
    }
  };

  const saveFiscal = async () => {
    setSaving(true);
    setFiscalError(null);
    try {
      await apiService.updateCustomer(customer.id, {
        taxId: fiscalForm.taxId || undefined,
        ivaCondition: fiscalForm.ivaCondition,
      });
      setEditingFiscal(false);
      await onSaved();
    } catch (e: any) {
      setFiscalError(e?.response?.data?.message ?? e.message);
    } finally {
      setSaving(false);
    }
  };

  const saveBank = async () => {
    setSaving(true);
    setBankError(null);
    const digits = bankForm.cbu.replace(/\D/g, '');
    if (digits && digits.length !== 22) {
      setBankError('El CBU debe tener exactamente 22 dígitos');
      setSaving(false);
      return;
    }
    try {
      await apiService.updateCustomer(customer.id, {
        cbu: digits || null,
      } as any);
      setEditingBank(false);
      await onSaved();
    } catch (e: any) {
      setBankError(e?.response?.data?.message ?? e.message);
    } finally {
      setSaving(false);
    }
  };

  const saveFinancial = async () => {
    setSaving(true);
    setFinancialError(null);
    try {
      await apiService.updateCustomer(customer.id, {
        monthlyIncome: financialForm.monthlyIncome
          ? parseMoneyInput(financialForm.monthlyIncome)
          : undefined,
        monthlyExpenses: financialForm.monthlyExpenses
          ? parseMoneyInput(financialForm.monthlyExpenses)
          : undefined,
        employmentStatus: financialForm.employmentStatus || undefined,
        employmentYears: financialForm.employmentYears ? Number(financialForm.employmentYears) : undefined,
      });
      setEditingFinancial(false);
      await onSaved();
    } catch (e: any) {
      setFinancialError(e?.response?.data?.message ?? e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-700 uppercase">Personal</h3>
          {!editingPersonal && (
            <button
              type="button"
              onClick={() => {
                setPersonalForm({
                  firstName: customer.firstName ?? '',
                  lastName: customer.lastName ?? '',
                  email: customer.email ?? '',
                  phone: customer.phone ?? '',
                  birthDate: customer.birthDate?.slice(0, 10) ?? '',
                  gender: customer.gender ?? 'X',
                  documentType: customer.documentType ?? 'DNI',
                  documentNumber: customer.documentNumber ?? '',
                });
                setEditingPersonal(true);
              }}
              className="text-xs text-blue-600 hover:underline"
            >
              Editar
            </button>
          )}
        </div>
        {personalError && <p className="text-xs text-red-600 mb-2">{personalError}</p>}
        {editingPersonal ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-gray-500">Nombre</label>
                <input
                  value={personalForm.firstName}
                  onChange={(e) => setPersonalForm({ ...personalForm, firstName: e.target.value })}
                  className="w-full mt-1 px-2 py-1 border rounded text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">Apellido</label>
                <input
                  value={personalForm.lastName}
                  onChange={(e) => setPersonalForm({ ...personalForm, lastName: e.target.value })}
                  className="w-full mt-1 px-2 py-1 border rounded text-sm"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-500">Email</label>
              <input
                type="email"
                value={personalForm.email}
                onChange={(e) => setPersonalForm({ ...personalForm, email: e.target.value })}
                className="w-full mt-1 px-2 py-1 border rounded text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500">Teléfono</label>
              <input
                value={personalForm.phone}
                onChange={(e) => setPersonalForm({ ...personalForm, phone: e.target.value })}
                className="w-full mt-1 px-2 py-1 border rounded text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-gray-500">Fecha de nacimiento</label>
                <input
                  type="date"
                  value={personalForm.birthDate}
                  onChange={(e) => setPersonalForm({ ...personalForm, birthDate: e.target.value })}
                  className="w-full mt-1 px-2 py-1 border rounded text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">Género</label>
                <select
                  value={personalForm.gender}
                  onChange={(e) => setPersonalForm({ ...personalForm, gender: e.target.value })}
                  className="w-full mt-1 px-2 py-1 border rounded text-sm"
                >
                  <option value="M">M</option>
                  <option value="F">F</option>
                  <option value="X">X</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-gray-500">Tipo doc.</label>
                <input
                  value={personalForm.documentType}
                  onChange={(e) => setPersonalForm({ ...personalForm, documentType: e.target.value })}
                  className="w-full mt-1 px-2 py-1 border rounded text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">DNI / documento</label>
                <input
                  value={personalForm.documentNumber}
                  onChange={(e) => setPersonalForm({ ...personalForm, documentNumber: e.target.value })}
                  className="w-full mt-1 px-2 py-1 border rounded text-sm font-mono"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={savePersonal}
                disabled={saving || !personalForm.firstName.trim() || !personalForm.lastName.trim() || !personalForm.email.trim()}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
              <button
                type="button"
                onClick={() => setEditingPersonal(false)}
                className="px-3 py-1 text-sm border rounded hover:bg-gray-50"
              >
                Cancelar
              </button>
            </div>
          </div>
        ) : (
          <>
            <Row label="Nombre" value={`${customer.firstName} ${customer.lastName}`} />
            <Row label="Email" value={customer.email} />
            <Row label="Teléfono" value={customer.phone} />
            <Row label="Fecha de nacimiento" value={customer.birthDate?.slice(0, 10)} />
            <Row label="Género" value={customer.gender} />
            <Row label="Estado" value={customer.status} />
            <Row label="DNI/CUIT" value={customer.documentNumber} />
            <Row label="Creado" value={customer.createdAt?.slice(0, 10)} />
          </>
        )}
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-700 uppercase">Dirección</h3>
          {!editingAddress && (
            <button
              type="button"
              onClick={() => {
                setAddressForm({
                  address: customer.address ?? '',
                  city: customer.city ?? '',
                  state: customer.state ?? '',
                  zipCode: customer.zipCode ?? '',
                  country: customer.country ?? '',
                });
                setEditingAddress(true);
              }}
              className="text-xs text-blue-600 hover:underline"
            >
              Editar
            </button>
          )}
        </div>
        {addressError && <p className="text-xs text-red-600 mb-2">{addressError}</p>}
        {editingAddress ? (
          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-500">Calle</label>
              <input
                value={addressForm.address}
                onChange={(e) => setAddressForm({ ...addressForm, address: e.target.value })}
                className="w-full mt-1 px-2 py-1 border rounded text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-gray-500">Ciudad</label>
                <input
                  value={addressForm.city}
                  onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                  className="w-full mt-1 px-2 py-1 border rounded text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">Provincia</label>
                <input
                  value={addressForm.state}
                  onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value })}
                  className="w-full mt-1 px-2 py-1 border rounded text-sm"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-gray-500">CP</label>
                <input
                  value={addressForm.zipCode}
                  onChange={(e) => setAddressForm({ ...addressForm, zipCode: e.target.value })}
                  className="w-full mt-1 px-2 py-1 border rounded text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">País</label>
                <input
                  value={addressForm.country}
                  onChange={(e) => setAddressForm({ ...addressForm, country: e.target.value })}
                  className="w-full mt-1 px-2 py-1 border rounded text-sm"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={saveAddress}
                disabled={saving}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
              <button
                type="button"
                onClick={() => setEditingAddress(false)}
                className="px-3 py-1 text-sm border rounded hover:bg-gray-50"
              >
                Cancelar
              </button>
            </div>
          </div>
        ) : (
          <>
            <Row label="Calle" value={customer.address} />
            <Row label="Ciudad" value={customer.city} />
            <Row label="Provincia" value={customer.state} />
            <Row label="CP" value={customer.zipCode} />
            <Row label="País" value={customer.country} />
          </>
        )}
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-700 uppercase">Financiera</h3>
          {!editingFinancial && (
            <button
              type="button"
              onClick={() => {
                setFinancialForm({
                  monthlyIncome: customer.monthlyIncome != null ? String(customer.monthlyIncome) : '',
                  monthlyExpenses: customer.monthlyExpenses != null ? String(customer.monthlyExpenses) : '',
                  employmentStatus: customer.employmentStatus ?? '',
                  employmentYears: customer.employmentYears != null ? String(customer.employmentYears) : '',
                });
                setEditingFinancial(true);
              }}
              className="text-xs text-blue-600 hover:underline"
            >
              Editar
            </button>
          )}
        </div>
        {financialError && <p className="text-xs text-red-600 mb-2">{financialError}</p>}
        {editingFinancial ? (
          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-500">Ingreso mensual</label>
              <input
                type="number"
                value={financialForm.monthlyIncome}
                onChange={(e) => setFinancialForm({ ...financialForm, monthlyIncome: e.target.value })}
                className="w-full mt-1 px-2 py-1 border rounded text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500">Egresos mensuales</label>
              <input
                type="number"
                value={financialForm.monthlyExpenses}
                onChange={(e) => setFinancialForm({ ...financialForm, monthlyExpenses: e.target.value })}
                className="w-full mt-1 px-2 py-1 border rounded text-sm"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500">Estado laboral</label>
              <select
                value={financialForm.employmentStatus}
                onChange={(e) => setFinancialForm({ ...financialForm, employmentStatus: e.target.value })}
                className="w-full mt-1 px-2 py-1 border rounded text-sm"
              >
                {EMPLOYMENT_OPTIONS.map((opt) => (
                  <option key={opt.value || 'empty'} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500">Antigüedad (años)</label>
              <input
                type="number"
                min={0}
                value={financialForm.employmentYears}
                onChange={(e) => setFinancialForm({ ...financialForm, employmentYears: e.target.value })}
                className="w-full mt-1 px-2 py-1 border rounded text-sm"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={saveFinancial}
                disabled={saving}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
              <button
                type="button"
                onClick={() => setEditingFinancial(false)}
                className="px-3 py-1 text-sm border rounded hover:bg-gray-50"
              >
                Cancelar
              </button>
            </div>
          </div>
        ) : (
          <>
            <Row label="Ingreso mensual" value={money(customer.monthlyIncome)} />
            <Row label="Egresos mensuales" value={money(customer.monthlyExpenses)} />
            <Row
              label="Estado laboral"
              value={EMPLOYMENT_OPTIONS.find((o) => o.value === customer.employmentStatus)?.label ?? customer.employmentStatus}
            />
            <Row label="Antigüedad (años)" value={customer.employmentYears} />
            <Row label="Credit score" value={customer.creditScore} />
            <PayslipDerivedRows customerId={customer.id} refreshToken={customer.updatedAt ?? customer.monthlyIncome} />
            <p className="text-xs text-gray-500 mt-2">Al confirmar un recibo: ingreso + estado Empleado; período y día de cobro quedan abajo.</p>
          </>
        )}
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-700 uppercase">Datos fiscales</h3>
          {!editingFiscal && (
            <button
              type="button"
              onClick={() => {
                setFiscalForm({
                  taxId: customer.taxId ?? '',
                  ivaCondition: customer.ivaCondition ?? 'CONSUMIDOR_FINAL',
                });
                setEditingFiscal(true);
              }}
              className="text-xs text-blue-600 hover:underline"
            >
              Editar
            </button>
          )}
        </div>
        {fiscalError && (
          <p className="text-xs text-red-600 mb-2">{fiscalError}</p>
        )}
        {editingFiscal ? (
          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-500">CUIT (obligatorio si es RI)</label>
              <input
                value={fiscalForm.taxId}
                onChange={(e) => setFiscalForm({ ...fiscalForm, taxId: e.target.value })}
                className="w-full mt-1 px-2 py-1 border rounded text-sm font-mono"
                placeholder="20-12345678-9"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500">Condición IVA</label>
              <select
                value={fiscalForm.ivaCondition}
                onChange={(e) => setFiscalForm({ ...fiscalForm, ivaCondition: e.target.value })}
                className="w-full mt-1 px-2 py-1 border rounded text-sm"
              >
                {IVA_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <p className="text-xs text-gray-500">
              Clientes RI reciben <strong>Factura A</strong> al cobrar interés o punitorio.
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={saveFiscal}
                disabled={saving}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
              <button
                type="button"
                onClick={() => setEditingFiscal(false)}
                className="px-3 py-1 text-sm border rounded hover:bg-gray-50"
              >
                Cancelar
              </button>
            </div>
          </div>
        ) : (
          <>
            <Row
              label="Condición IVA"
              value={IVA_OPTIONS.find((o) => o.value === customer.ivaCondition)?.label ?? customer.ivaCondition ?? 'Consumidor Final'}
            />
            <Row label="CUIT fiscal" value={customer.taxId ?? '—'} />
          </>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-700 uppercase">Cuenta bancaria (débito / transferencia)</h3>
          {!editingBank && (
            <button
              type="button"
              onClick={() => {
                setBankForm({ cbu: customer.cbu ?? '' });
                setEditingBank(true);
              }}
              className="text-xs text-blue-600 hover:underline"
            >
              Editar
            </button>
          )}
        </div>
        {bankError && <p className="text-xs text-red-600 mb-2">{bankError}</p>}
        {editingBank ? (
          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-500">CBU (22 dígitos)</label>
              <input
                value={bankForm.cbu}
                onChange={(e) => setBankForm({ cbu: e.target.value.replace(/[^\d]/g, '').slice(0, 22) })}
                className="w-full mt-1 px-2 py-1 border rounded text-sm font-mono"
                placeholder="0000000000000000000000"
                inputMode="numeric"
              />
              <p className="text-xs text-gray-500 mt-1">
                Necesario para generar lotes BAPRO / Emp Pago Directo y referenciar transferencias al cliente.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={saveBank}
                disabled={saving}
                className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
              <button
                type="button"
                onClick={() => setEditingBank(false)}
                className="px-3 py-1 text-sm border rounded hover:bg-gray-50"
              >
                Cancelar
              </button>
            </div>
          </div>
        ) : (
          <>
            <Row
              label="CBU"
              value={
                customer.cbu
                  ? `${String(customer.cbu).slice(0, 6)}…${String(customer.cbu).slice(-4)}`
                  : '—'
              }
            />
            {!customer.cbu ? (
              <p className="text-xs text-amber-700 mt-2">
                Sin CBU el cliente queda excluido de débitos Provincia / archivo al banco.
              </p>
            ) : null}
          </>
        )}
      </div>

      <CustomerOriginationDocs
        customerId={customer.id}
        customerDocumentNumber={customer.documentNumber}
        onChanged={onSaved}
      />
    </div>
  );
}

function LoansTab({ loans }: { loans: any[] }) {
  const navigate = useNavigate();
  if (loans.length === 0) return <p className="text-gray-500 text-sm">Sin préstamos.</p>;
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Crédito</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Monto</th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Plazo / Tasa</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {loans.map((l) => {
            const date = (l.disbursementDate ?? l.createdAt)?.slice?.(0, 10) ?? '—';
            const product = l.product?.shortName || l.product?.name || l.product?.planCode || '—';
            return (
              <tr
                key={l.id}
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => navigate(`/loans/${l.id}`)}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{loanShortRef(l)}</div>
                  <div className="text-xs text-gray-500 font-mono">{String(l.id).slice(0, 10)}…</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{product}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">{money(l.amount)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                  {l.termMonths}m · {(Number(l.interestRate) * 100).toFixed(1)}%
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{date}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ' + (STATUS[l.status] ?? 'bg-gray-100 text-gray-800')}>{l.status}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PaymentsTab({
  customerId,
  loans,
  onTotalChange,
}: {
  customerId: string;
  loans: any[];
  onTotalChange?: (total: number) => void;
}) {
  const navigate = useNavigate();
  const [payments, setPayments] = useState<any[]>([]);
  const [loanId, setLoanId] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [meta, setMeta] = useState({ total: 0, page: 1, pageSize: 25, pageCount: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiService.listPayments({
          customerId,
          page,
          pageSize,
          ...(loanId ? { loanId } : {}),
        });
        if (cancelled) return;
        setPayments(res.data);
        setMeta(res.meta);
        onTotalChange?.(res.meta.total);
      } catch (e: any) {
        if (!cancelled) setError(e?.response?.data?.message ?? e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customerId, page, pageSize, loanId]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm text-gray-600 flex items-center gap-2">
          Crédito
          <select
            value={loanId}
            onChange={(e) => {
              setPage(1);
              setLoanId(e.target.value);
            }}
            className="px-3 py-1.5 border rounded-md text-sm min-w-[16rem]"
          >
            <option value="">Todos</option>
            {loans.map((l) => (
              <option key={l.id} value={l.id}>{loanLabel(l)}</option>
            ))}
          </select>
        </label>
      </div>

      {error && (
        <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
      )}

      {loading ? (
        <p className="text-gray-500 text-sm">Cargando pagos…</p>
      ) : payments.length === 0 ? (
        <p className="text-gray-500 text-sm">Sin pagos{loanId ? ' para este crédito' : ''}.</p>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Crédito</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Monto</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Capital</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Interés</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Punitorio</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Método</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {payments.map((p) => (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{p.paymentDate?.slice(0, 10)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {p.loan?.id ? (
                      <button
                        type="button"
                        className="text-blue-700 hover:underline text-left"
                        onClick={() => navigate(`/loans/${p.loan.id}`)}
                        title={loanLabel(p.loan)}
                      >
                        <span className="font-medium">{loanShortRef(p.loan)}</span>
                        <span className="block text-xs text-gray-500">{money(p.loan.amount)}</span>
                      </button>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">{money(p.amount)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right">{money(p.principalAmount)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right">{money(p.interestAmount)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right">
                    {p.penaltyAmount ? <span className="text-orange-700">{money(p.penaltyAmount)}</span> : '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{p.paymentMethod ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t bg-gray-50 text-sm">
            <div className="text-gray-600">
              Página {meta.page} de {meta.pageCount} · {meta.total.toLocaleString('es-AR')} pagos
            </div>
            <div className="flex items-center gap-2">
              <select
                value={pageSize}
                onChange={(e) => {
                  setPage(1);
                  setPageSize(Number(e.target.value));
                }}
                className="px-2 py-1 border rounded-md"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <button
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1 border rounded-md disabled:opacity-40"
              >
                Anterior
              </button>
              <button
                type="button"
                disabled={page >= meta.pageCount || loading}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1 border rounded-md disabled:opacity-40"
              >
                Siguiente
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KycTab({ kycChecks }: { kycChecks: any[] }) {
  if (kycChecks.length === 0) return <p className="text-gray-500 text-sm">Sin verificaciones KYC.</p>;
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">CheckType</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Resultado</th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Score</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {kycChecks.map((k) => (
            <tr key={k.id}>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{k.createdAt?.slice(0, 10)}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{k.type}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{k.checkType}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{k.result ?? '—'}</td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">{k.score != null ? (k.score * 100).toFixed(0) + '%' : '—'}</td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ' + (STATUS[k.status] ?? 'bg-gray-100 text-gray-800')}>{k.status}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BureauTab({
  customer,
  queries,
  loading,
  error,
  provider,
  onProviderChange,
  onQuery,
}: {
  customer: any;
  queries: any[];
  loading: boolean;
  error: string | null;
  provider: string;
  onProviderChange: (v: string) => void;
  onQuery: () => void;
}) {
  const providerLabel = provider === 'RIESGO_ONLINE' ? 'Riesgo OnLine' : 'Veraz';
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg border p-4 flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-sm text-gray-700">
            Documento: <strong>{customer.documentNumber ?? '—'}</strong>
          </p>
          <p className="text-xs text-gray-500 mt-1">Consulta al buró de crédito (Veraz o Riesgo OnLine).</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={provider}
            onChange={(e) => onProviderChange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm"
          >
            <option value="VERAZ">Veraz</option>
            <option value="RIESGO_ONLINE">Riesgo OnLine</option>
          </select>
          <button
            onClick={onQuery}
            disabled={loading || !customer.documentNumber}
            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Consultando…' : `Consultar ${providerLabel}`}
          </button>
        </div>
      </div>

      {error && <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      {queries.length === 0 ? (
        <p className="text-gray-500 text-sm">Sin consultas previas para este documento.</p>
      ) : (
        <div className="bg-white rounded-lg border overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Proveedor</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Resultado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {queries.map((q) => (
                <tr key={q.id}>
                  <td className="px-4 py-2 text-sm">{q.createdAt?.slice?.(0, 19).replace('T', ' ')}</td>
                  <td className="px-4 py-2 text-sm">{q.provider ?? '—'}</td>
                  <td className="px-4 py-2 text-sm">
                    <pre className="text-xs whitespace-pre-wrap max-w-xl">{JSON.stringify(q.response ?? q.result ?? q, null, 2).slice(0, 500)}</pre>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
